import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Input} from '@/components/ui/input'
import {SearchComboboxShell} from '@/components/search-combobox-shell'
import {normalize} from '@/lib/utils'
import type {BateriaPaquete} from '@/hooks/bateria/use-bateria'
import {tope} from '@/lib/campos-texto'

export type PaqueteFiltro = Pick<BateriaPaquete, 'id' | 'employee_name' | 'employee_cedula'>

/** R3: combobox buscable multiselección; no aplica nada solo, quien lo monta decide cuándo filtrar. */
export function PaquetesMultiSelect({
  paquetes,
  selectedIds,
  onChange,
  testId,
}: {
  paquetes: PaqueteFiltro[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  testId: string
}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const normalizedQuery = normalize(query)

  const matches = useMemo(
    () => paquetes.filter((p) => normalize(`${p.employee_name} ${p.employee_cedula}`).includes(normalizedQuery)),
    [paquetes, normalizedQuery]
  )

  function toggle(id: string) {
    onChange(selectedIds.includes(id) ? selectedIds.filter((otro) => otro !== id) : [...selectedIds, id])
  }

  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (!next) setQuery('')
  }

  return (
    <SearchComboboxShell
      title={t('admin.bateria.cola.filtros.panel.elegirPersonas')}
      open={open}
      onOpenChange={handleOpenChange}
      trigger={
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={`${testId}-trigger`}
          className="flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-input bg-transparent px-2.5 text-sm text-muted-foreground hover:border-[color-mix(in_oklch,var(--input),var(--foreground)_18%)] hover:text-foreground">
          {t('admin.bateria.cola.filtros.panel.elegirPersonas')}
          {selectedIds.length > 0 && <span className="font-mono text-xs">({selectedIds.length})</span>}
        </button>
      }>
      <Input
        maxLength={tope('BUSCA')}
        placeholder={t('admin.bateria.cola.filtros.panel.buscarPersonas')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoComplete="off"
        autoFocus
        data-testid={`${testId}-search`}
        className="h-12 shrink-0 rounded-none border-0 border-b border-border text-base focus-visible:ring-0 md:h-9 md:text-sm"
      />
      <ul className="flex-1 overflow-y-auto p-1 md:max-h-56 md:flex-none">
        {matches.map((p) => (
          <li key={p.id}>
            {/* El testid va en el `<label>`: `.filter({ hasText })` de Playwright no encuentra un checkbox pelado. */}
            <label
              data-testid={`${testId}-option`}
              className="flex items-center gap-2 rounded-md px-2 py-3 text-left text-sm hover:bg-secondary md:py-1.5">
              <input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggle(p.id)} />
              <span className="min-w-0 truncate">{p.employee_name}</span>
              <span className="ml-auto shrink-0 font-mono text-xs text-muted-foreground">{p.employee_cedula}</span>
            </label>
          </li>
        ))}
        {matches.length === 0 && <li className="px-2 py-1.5 text-sm text-muted-foreground">{t('common.noResults')}</li>}
      </ul>
    </SearchComboboxShell>
  )
}
